import { Activity } from '@core/models/activity.model';
import { TranslateService } from '@ngx-translate/core';

type Translate = Pick<TranslateService, 'instant'>;

export const ACTIVITY_STATUSES = ['Success', 'Failed', 'Pending', 'In Progress'] as const;

const STATUS_LABEL_KEYS: Record<string, string> = {
  Success: 'general.completed',
  Failed: 'general.failed',
  Pending: 'general.pending',
  'In Progress': 'general.inProgress',
};

const DEVICE_ACTIONS = new Set([
  'clear_device_config',
  'lan_connection_test',
  'update_settings',
  'pol',
  'send_initial_config',
  'sim_connection_test',
  'verification',
  'timezone_config',
  'send_device_config',
  'update_meter_role',
  'firmware_update',
  'sim_topup',
  'sim_extension',
  'timezone_mismatch',
  'timezone_defaulted',
]);

export function activityStatusLabel(translate: Translate, status: string): string {
  const key = STATUS_LABEL_KEYS[status];
  return key ? translate.instant(key) : status;
}

export function deviceActionLabel(translate: Translate, action: string): string {
  return DEVICE_ACTIONS.has(action)
    ? translate.instant('devices.detailTabs.activity.actions.' + action)
    : action;
}

// An object shows as its JSON rather than '[object Object]'.
export function scalarText(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function isEmpty(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys(value as Record<string, unknown>).length === 0)
  );
}

// A command's parameters are named `$name$` in the message; anything else shows its scalar fields
// and only the names of its nested ones.
function summarizeMessage(message: string): string {
  try {
    const parsed = JSON.parse(message);
    const params: { name: string; value: unknown }[] = parsed.dynamicParameterInputs ?? [];
    if (params.length > 0) {
      return params
        .map(p => {
          const n = String(p.name);
          return `${n.startsWith('$') && n.endsWith('$') ? n.slice(1, -1) : n}: ${p.value}`;
        })
        .join(', ');
    }
    const parts = Object.entries(parsed as Record<string, unknown>)
      .filter(([, v]) => !isEmpty(v))
      .map(([k, v]) => (Array.isArray(v) || typeof v === 'object' ? k : `${k}: ${scalarText(v)}`));
    return parts.join(', ') || message;
  } catch {
    return message;
  }
}

function connectionTestResult(translate: Translate, message: string): string {
  if (message.includes("'successful': True")) return translate.instant('general.successful');
  if (message.includes("'successful': False")) return translate.instant('general.failed');
  return message;
}

export function deviceActivitySummary(
  translate: Translate,
  activity: Pick<Activity, 'action' | 'message'>,
): string {
  if (activity.action === 'lan_connection_test' || activity.action === 'sim_connection_test') {
    return connectionTestResult(translate, activity.message ?? '');
  }
  return activity.message ? summarizeMessage(activity.message) : '';
}
